import React from 'react';
import { Controller, Control, UseFormSetValue } from 'react-hook-form';

import { EntityFilterSet, EntityFilters } from 'types/notification_rule';

import { useCameras } from 'cameras/hooks/useCameras';
import { useDeployments } from 'deployments/hooks/useDeployments';
import { useGateways } from 'gateways/hooks/useGateways';
import { usePipelines } from 'pipelines/hooks/usePipelines';
import { useStreams } from 'streams/hooks/useStreams';

import { Button } from 'components/Button/Button';
import { Field } from 'components/Field/Field';
import { FormMessage } from 'components/FormMessage/FormMessage';
import { Icon } from 'components/Icon/IconDeprecated';
import { IconButton } from 'components/IconButton/IconButtonDeprecated';
import { Loader } from 'components/Loader/Loader';
import { Select } from 'components/Select/Select';
import { CameraSelect } from 'cameras/components/CameraSelect/CameraSelect';
import { DeploymentSelect } from 'deployments/components/DeploymentSelect/DeploymentSelect';
import { GatewaySelect } from 'gateways/components/GatewaySelect/GatewaySelect';
import { PipelineSelect } from 'pipelines/components/PipelineSelect/PipelineSelect';
import { StreamSelect } from 'streams/components/StreamSelect/StreamSelect';
import { TagSelect } from 'tags/components/TagSelect/TagSelect';

type EntityType =
  | 'gateway_ids'
  | 'camera_ids'
  | 'stream_ids'
  | 'deployment_ids'
  | 'pipeline_ids'
  | 'tag_ids';

type FilterMode = 'include' | 'exclude';

type FilterModeOption = { value: FilterMode; label: string };

type EntityFilterRow = {
  entityType: EntityType | 'undecided';
  mode: FilterMode;
};

const ENTITY_FILTER_OPTIONS: Array<{ label: string; value: EntityType }> = [
  { label: 'Gateways', value: 'gateway_ids' },
  { label: 'Cameras', value: 'camera_ids' },
  { label: 'Streams', value: 'stream_ids' },
  { label: 'Deployments', value: 'deployment_ids' },
  { label: 'Workflows', value: 'pipeline_ids' },
  { label: 'Tags', value: 'tag_ids' },
];

const FILTER_MODE_OPTIONS: FilterModeOption[] = [
  { value: 'include', label: 'Include' },
  { value: 'exclude', label: 'Exclude' },
];

export type EntityFilterFieldValues = {
  entity_filter_gateway_ids: string[];
  entity_filter_camera_ids: string[];
  entity_filter_stream_ids: string[];
  entity_filter_deployment_ids: string[];
  entity_filter_pipeline_ids: string[];
  entity_filter_tag_ids: string[];
  entity_filter_gateway_ids_mode: FilterMode;
  entity_filter_camera_ids_mode: FilterMode;
  entity_filter_stream_ids_mode: FilterMode;
  entity_filter_deployment_ids_mode: FilterMode;
  entity_filter_pipeline_ids_mode: FilterMode;
  entity_filter_tag_ids_mode: FilterMode;
};

export const ENTITY_FILTER_DEFAULT_VALUES: EntityFilterFieldValues = {
  entity_filter_gateway_ids: [],
  entity_filter_camera_ids: [],
  entity_filter_stream_ids: [],
  entity_filter_deployment_ids: [],
  entity_filter_pipeline_ids: [],
  entity_filter_tag_ids: [],
  entity_filter_gateway_ids_mode: 'include',
  entity_filter_camera_ids_mode: 'include',
  entity_filter_stream_ids_mode: 'include',
  entity_filter_deployment_ids_mode: 'include',
  entity_filter_pipeline_ids_mode: 'include',
  entity_filter_tag_ids_mode: 'include',
};

function getFieldName(entityType: EntityType) {
  return `entity_filter_${entityType}` as keyof EntityFilterFieldValues;
}

function getModeFieldName(entityType: EntityType) {
  return `entity_filter_${entityType}_mode` as keyof EntityFilterFieldValues;
}

/** Build EntityFilters from field values for API submission. */
export function buildEntityFilters(
  values: EntityFilterFieldValues
): EntityFilters | null {
  const include: EntityFilterSet = {};
  const exclude: EntityFilterSet = {};

  for (const { value: entityType } of ENTITY_FILTER_OPTIONS) {
    const ids = values[getFieldName(entityType)] as string[];
    if (!ids || ids.length === 0) continue;

    const mode = values[getModeFieldName(entityType)] as FilterMode;
    if (mode === 'exclude') {
      exclude[entityType] = ids;
    } else {
      include[entityType] = ids;
    }
  }

  const hasInclude = Object.keys(include).length > 0;
  const hasExclude = Object.keys(exclude).length > 0;

  if (!hasInclude && !hasExclude) return null;

  return {
    include: hasInclude ? include : null,
    exclude: hasExclude ? exclude : null,
  };
}

/** Parse EntityFilters into field values for form initialization. */
export function parseEntityFilters(
  filters: EntityFilters | null | undefined
): Partial<EntityFilterFieldValues> {
  if (!filters) return {};

  const values: Partial<EntityFilterFieldValues> = {};

  for (const { value: entityType } of ENTITY_FILTER_OPTIONS) {
    const includeIds = filters.include?.[entityType];
    const excludeIds = filters.exclude?.[entityType];

    if (includeIds && includeIds.length > 0) {
      (values as Record<string, unknown>)[getFieldName(entityType)] =
        includeIds;
      (values as Record<string, unknown>)[getModeFieldName(entityType)] =
        'include';
    } else if (excludeIds && excludeIds.length > 0) {
      (values as Record<string, unknown>)[getFieldName(entityType)] =
        excludeIds;
      (values as Record<string, unknown>)[getModeFieldName(entityType)] =
        'exclude';
    }
  }

  return values;
}

/** Determine which entity filter rows to show initially. */
export function getInitialFilterRows(
  filters: EntityFilters | null | undefined
): EntityFilterRow[] {
  if (!filters) return [];

  const rows: EntityFilterRow[] = [];

  for (const { value: entityType } of ENTITY_FILTER_OPTIONS) {
    const includeIds = filters.include?.[entityType];
    const excludeIds = filters.exclude?.[entityType];

    if (includeIds && includeIds.length > 0) {
      rows.push({ entityType, mode: 'include' });
    } else if (excludeIds && excludeIds.length > 0) {
      rows.push({ entityType, mode: 'exclude' });
    }
  }

  return rows;
}

type EntityFiltersSectionProps = {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  initialEntityFilters?: EntityFilters | null;
};

export function EntityFiltersSection({
  control,
  setValue,
  initialEntityFilters,
}: EntityFiltersSectionProps) {
  const [filterRows, setFilterRows] = React.useState<EntityFilterRow[]>(() =>
    getInitialFilterRows(initialEntityFilters)
  );

  React.useEffect(() => {
    setFilterRows(getInitialFilterRows(initialEntityFilters));
  }, [initialEntityFilters]);

  // Pre-fetch entities for existing filter values
  const existingGatewayIds = React.useMemo(
    () =>
      initialEntityFilters?.include?.gateway_ids ??
      initialEntityFilters?.exclude?.gateway_ids ??
      [],
    [initialEntityFilters]
  );
  const existingCameraIds = React.useMemo(
    () =>
      initialEntityFilters?.include?.camera_ids ??
      initialEntityFilters?.exclude?.camera_ids ??
      [],
    [initialEntityFilters]
  );
  const existingStreamIds = React.useMemo(
    () =>
      initialEntityFilters?.include?.stream_ids ??
      initialEntityFilters?.exclude?.stream_ids ??
      [],
    [initialEntityFilters]
  );
  const existingDeploymentIds = React.useMemo(
    () =>
      initialEntityFilters?.include?.deployment_ids ??
      initialEntityFilters?.exclude?.deployment_ids ??
      [],
    [initialEntityFilters]
  );
  const existingPipelineIds = React.useMemo(
    () =>
      initialEntityFilters?.include?.pipeline_ids ??
      initialEntityFilters?.exclude?.pipeline_ids ??
      [],
    [initialEntityFilters]
  );

  const { data: initialGateways, isInitialLoading: isGatewaysLoading } =
    useGateways({
      params: { gateway_ids: existingGatewayIds, limit: existingGatewayIds.length },
      enabled: existingGatewayIds.length > 0,
    });

  const { data: initialCameras, isInitialLoading: isCamerasLoading } =
    useCameras({
      params: { camera_ids: existingCameraIds, limit: existingCameraIds.length },
      enabled: existingCameraIds.length > 0,
    });

  const { data: initialStreams, isInitialLoading: isStreamsLoading } =
    useStreams({
      params: { stream_ids: existingStreamIds, limit: existingStreamIds.length },
      enabled: existingStreamIds.length > 0,
    });

  const { data: initialDeployments, isInitialLoading: isDeploymentsLoading } =
    useDeployments({
      params: {
        deployment_ids: existingDeploymentIds,
        limit: existingDeploymentIds.length,
      },
      enabled: existingDeploymentIds.length > 0,
      keepPreviousData: true,
    });

  const { data: initialPipelines, isInitialLoading: isPipelinesLoading } =
    usePipelines({
      params: {
        pipeline_ids: existingPipelineIds,
        limit: existingPipelineIds.length,
      },
      enabled: existingPipelineIds.length > 0,
      keepPreviousData: true,
    });

  const isLoading =
    isGatewaysLoading ||
    isCamerasLoading ||
    isStreamsLoading ||
    isDeploymentsLoading ||
    isPipelinesLoading;

  // Detect stale (deleted) entities in existing filters
  const staleEntityWarnings = React.useMemo(() => {
    if (isLoading) return [];
    const warnings: string[] = [];

    function checkStale(
      label: string,
      requestedIds: string[],
      fetchedData: { data: Array<{ id: string }> } | undefined
    ) {
      if (requestedIds.length === 0) return;
      const fetchedIds = new Set(fetchedData?.data?.map((e) => e.id) ?? []);
      const missing = requestedIds.filter((id) => !fetchedIds.has(id));
      if (missing.length > 0) {
        warnings.push(
          `${missing.length} ${label} in this filter no longer exist and will not match.`
        );
      }
    }

    checkStale('gateway(s)', existingGatewayIds, initialGateways);
    checkStale('camera(s)', existingCameraIds, initialCameras);
    checkStale('stream(s)', existingStreamIds, initialStreams);
    checkStale('deployment(s)', existingDeploymentIds, initialDeployments);
    checkStale('workflow(s)', existingPipelineIds, initialPipelines);

    return warnings;
  }, [
    isLoading,
    existingGatewayIds, initialGateways,
    existingCameraIds, initialCameras,
    existingStreamIds, initialStreams,
    existingDeploymentIds, initialDeployments,
    existingPipelineIds, initialPipelines,
  ]);

  const usedEntityTypes = new Set(
    filterRows
      .filter((r) => r.entityType !== 'undecided')
      .map((r) => r.entityType)
  );

  const availableEntityOptions = ENTITY_FILTER_OPTIONS.filter(
    (opt) => !usedEntityTypes.has(opt.value)
  );

  const canAddFilter =
    filterRows.length < ENTITY_FILTER_OPTIONS.length &&
    filterRows.at(-1)?.entityType !== 'undecided';

  function handleAddRow() {
    setFilterRows((prev) => [...prev, { entityType: 'undecided', mode: 'include' }]);
  }

  function handleRemoveRow(index: number) {
    setFilterRows((prev) => {
      const row = prev[index];
      if (row.entityType !== 'undecided') {
        setValue(getFieldName(row.entityType as EntityType), []);
        setValue(getModeFieldName(row.entityType as EntityType), 'include');
      }
      return prev.filter((_, i) => i !== index);
    });
  }

  function handleEntityTypeChange(
    index: number,
    newEntityType: EntityType
  ) {
    setFilterRows((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        // Clear old entity IDs if switching type
        if (row.entityType !== 'undecided') {
          setValue(getFieldName(row.entityType as EntityType), []);
        }
        return { ...row, entityType: newEntityType };
      })
    );
  }

  function handleModeChange(index: number, newMode: FilterMode) {
    setFilterRows((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        if (row.entityType !== 'undecided') {
          setValue(getModeFieldName(row.entityType as EntityType), newMode);
        }
        return { ...row, mode: newMode };
      })
    );
  }

  function renderEntityPicker(entityType: EntityType, fieldName: string) {
    switch (entityType) {
      case 'gateway_ids':
        return (
          <Controller
            control={control}
            name={fieldName}
            render={({ field: { onChange } }) => (
              <GatewaySelect
                defaultValue={initialGateways?.data}
                onChange={(options) =>
                  options && onChange(options.map((g) => g.id))
                }
                isMulti
              />
            )}
          />
        );
      case 'camera_ids':
        return (
          <Controller
            control={control}
            name={fieldName}
            render={({ field: { onChange } }) => (
              <CameraSelect
                defaultValue={initialCameras?.data}
                onChange={(options) =>
                  options && onChange(options.map((c) => c.id))
                }
                isMulti
              />
            )}
          />
        );
      case 'stream_ids':
        return (
          <Controller
            control={control}
            name={fieldName}
            render={({ field: { onChange } }) => (
              <StreamSelect
                defaultValue={initialStreams?.data}
                onChange={(options) =>
                  options && onChange(options.map((s) => s.id))
                }
                isMulti
              />
            )}
          />
        );
      case 'deployment_ids':
        return (
          <Controller
            control={control}
            name={fieldName}
            render={({ field: { onChange } }) => (
              <DeploymentSelect
                defaultValue={initialDeployments?.data}
                onChange={(options) =>
                  options && onChange(options.map((d) => d.id))
                }
                isMulti
              />
            )}
          />
        );
      case 'pipeline_ids':
        return (
          <Controller
            control={control}
            name={fieldName}
            render={({ field: { onChange } }) => (
              <PipelineSelect
                defaultValue={initialPipelines?.data}
                onChange={(options) =>
                  options && onChange(options.map((p) => p.id))
                }
                isMulti
              />
            )}
          />
        );
      case 'tag_ids':
        return (
          <Controller
            control={control}
            name={fieldName}
            render={({ field: { value, onChange } }) => (
              <TagSelect defaultValue={value} onChange={onChange} />
            )}
          />
        );
      default:
        return <Select options={[]} isDisabled />;
    }
  }

  return (
    <Field label="Entity filters">
      {isLoading ? (
        <Loader size="small" text="Loading filters" />
      ) : (
        <>
          {staleEntityWarnings.map((warning) => (
            <FormMessage key={warning} icon="warning" intent="warning">
              {warning}
            </FormMessage>
          ))}
          <div className="flex flex-col gap-2">
            {filterRows.map((row, index) => (
              <div
                className="grid grid-cols-[8rem_6rem_1fr_auto] items-center gap-2"
                key={`${row.entityType}-${index}`}
              >
                <Select
                  aria-label="Select entity type"
                  options={
                    row.entityType !== 'undecided'
                      ? [
                          ...availableEntityOptions,
                          ENTITY_FILTER_OPTIONS.find(
                            (o) => o.value === row.entityType
                          )!,
                        ]
                      : availableEntityOptions
                  }
                  value={ENTITY_FILTER_OPTIONS.find(
                    ({ value }) => value === row.entityType
                  )}
                  onChange={(option) => {
                    if (!option) return;
                    handleEntityTypeChange(index, option.value);
                  }}
                  placeholder="Entity..."
                />

                <Select<FilterModeOption>
                  aria-label="Include or exclude"
                  options={FILTER_MODE_OPTIONS}
                  value={FILTER_MODE_OPTIONS.find(
                    (o) => o.value === row.mode
                  )}
                  onChange={(option) => {
                    if (!option) return;
                    handleModeChange(index, option.value);
                  }}
                />

                <div>
                  {row.entityType !== 'undecided' ? (
                    renderEntityPicker(
                      row.entityType,
                      getFieldName(row.entityType)
                    )
                  ) : (
                    <Select options={[]} isDisabled placeholder="Select entity type first" />
                  )}
                </div>

                <IconButton
                  icon="cancel"
                  label="Remove filter"
                  onClick={() => handleRemoveRow(index)}
                />
              </div>
            ))}
          </div>
          <div className="mt-2">
            <Button
              variant="secondary"
              size="small"
              onClick={handleAddRow}
              disabled={!canAddFilter}
            >
              <Icon name="plus" size="xsmall" />
              <span>Add entity filter</span>
            </Button>
          </div>
        </>
      )}
    </Field>
  );
}
